import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, roleConfig } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Building2, Users, Ticket, FolderKanban, AlertTriangle,
  Loader2, ArrowRight, Activity, ShieldCheck, LogIn,
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, sub, accent = 'gray' }) => {
  const accentMap = {
    gray: 'text-gray-600 bg-gray-100',
    green: 'text-[#5BA51E] bg-[#7FD02E]/10',
    blue: 'text-blue-600 bg-blue-100',
    purple: 'text-purple-600 bg-purple-100',
    rose: 'text-rose-600 bg-rose-100',
    amber: 'text-amber-600 bg-amber-100',
  };
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accentMap[accent]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { switchCompany, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/admin/platform-stats`)
      .then((res) => setStats(res.data))
      .catch(() => toast.error('Error al cargar estadísticas de plataforma'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!stats) return null;

  const handleEnter = async (company) => {
    try {
      await switchCompany(company.id);
      toast.success(`Trabajando como ${company.name}`);
      navigate('/dashboard');
      setTimeout(() => window.location.reload(), 200);
    } catch (e) {
      toast.error('No se pudo cambiar de empresa');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <ShieldCheck className="w-3.5 h-3.5 text-[#7FD02E]" />
            Vista plataforma
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Hola, {user?.first_name} 👋
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Panorama global de NextPlan en todas las empresas.
          </p>
        </div>
        <Button
          onClick={() => navigate('/empresas')}
          className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
        >
          <Building2 className="w-4 h-4 mr-2" /> Gestionar empresas
        </Button>
      </div>

      {/* Global stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label="Empresas"
          value={stats.companies.total}
          sub={`${stats.companies.active} activas · ${stats.companies.inactive} inactivas`}
          accent="green"
        />
        <StatCard
          icon={Users}
          label="Usuarios"
          value={stats.users.total}
          sub="Excluye administradores de plataforma"
          accent="blue"
        />
        <StatCard
          icon={Ticket}
          label="Tickets"
          value={stats.tickets.total}
          sub={`${stats.tickets.open} abiertos · ${stats.tickets.closed} cerrados`}
          accent="purple"
        />
        <StatCard
          icon={FolderKanban}
          label="Proyectos"
          value={stats.projects.total}
          sub={`${stats.projects.tasks} tareas totales`}
          accent="amber"
        />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={AlertTriangle}
          label="SLA incumplidos"
          value={stats.tickets.sla_breached}
          sub="Tickets fuera de plazo"
          accent="rose"
        />
        <StatCard
          icon={Activity}
          label="Solucionadores"
          value={stats.users.by_role?.solucionador || 0}
          sub="Equipos técnicos operativos"
          accent="green"
        />
        <StatCard
          icon={ShieldCheck}
          label="Jefaturas"
          value={stats.users.by_role?.jefatura || 0}
          sub="Supervisores y aprobadores"
          accent="purple"
        />
      </div>

      {/* Per-company breakdown */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#7FD02E]" /> Empresas
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/empresas')}
            >
              Ver todas <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {stats.per_company.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              Aún no hay empresas registradas.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {stats.per_company.map((c) => (
                <div
                  key={c.id}
                  className="p-4 rounded-xl border border-gray-100 hover:border-[#7FD02E]/30 hover:bg-[#7FD02E]/5 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div
                      className="flex items-center gap-3 cursor-pointer min-w-0 flex-1"
                      onClick={() => navigate(`/empresas/${c.id}`)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7FD02E]/20 to-[#7FD02E]/5 flex items-center justify-center text-[#7FD02E] font-semibold flex-shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate group-hover:text-[#5BA51E]">
                          {c.name}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate">{c.slug}</p>
                      </div>
                    </div>
                    {!c.is_active && (
                      <Badge variant="secondary" className="text-xs">Inactiva</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{c.user_count}</p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400">Usuarios</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{c.ticket_count}</p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400">Tickets</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{c.project_count}</p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400">Proyectos</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-xs"
                      onClick={() => navigate(`/empresas/${c.id}`)}
                    >
                      Ver detalle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs gap-1"
                      onClick={() => handleEnter(c)}
                    >
                      <LogIn className="w-3 h-3" /> Entrar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User role distribution */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Usuarios por rol</h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.users.by_role || {}).map(([role, count]) => (
              <div key={role} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 flex-1 min-w-[160px]">
                <Badge className={roleConfig[role]?.color}>
                  {roleConfig[role]?.label || role}
                </Badge>
                <span className="text-2xl font-bold text-gray-900 ml-auto">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
